import { useState } from 'react';
import type { CategoryItem } from '../lib/catalog-client';

interface CategoryChipsProps {
  /** Head categories to render (already sorted + sliced). */
  categories: CategoryItem[];
  /** Controlled selection. */
  value: CategoryItem[];
  /** Pro users multi-select; Free users are radio (single). */
  isPro: boolean;
  /** Max categories selectable right now (accounts for niches picked via search). */
  max: number;
  onChange: (value: CategoryItem[]) => void;
  /**
   * Show only the first N chips plus a «Ещё N» chip that reveals the rest.
   * Unset = every chip at once (Uzbekistan's onboarding, unchanged). Set on the
   * Kazakh first screen (2026-10-20), where the tiles share a phone-height
   * sheet with the ИИ block and must not push it below the fold.
   */
  collapsedCount?: number;
  /** Label of the reveal chip, given the number of hidden chips. */
  moreLabel?: (hidden: number) => string;
  /** The reveal chip hands over to a fuller picker instead of expanding here
   *  (the Kazakh first screen's «Ещё N», 2026-10-20). */
  onMore?: () => void;
  /** One line per chip, cut with «…» (full name in `title`) — so a long name
   *  cannot grow the block by a row. */
  singleLine?: boolean;
}

/**
 * Onboarding category picker — the discoverable "head" of the taxonomy. Renders
 * the top categories as tappable chips. Free = radio (one total); Pro = multi up
 * to `max`. The long tail is reached via the ИКПУ search fallback, not here.
 */
export function CategoryChips({
  categories,
  value,
  isPro,
  max,
  onChange,
  collapsedCount,
  moreLabel,
  onMore,
  singleLine = false,
}: CategoryChipsProps) {
  const selected = new Set(value.map((v) => v.slug));
  const [expanded, setExpanded] = useState(false);
  // A picked chip stays on screen even when it sits past the cut — hiding the
  // reader's own choice behind «Ещё» reads as if the tap was lost.
  const visible =
    collapsedCount === undefined || expanded
      ? categories
      : categories.filter((c, i) => i < collapsedCount || selected.has(c.slug));
  const hidden = categories.length - visible.length;

  function toggle(cat: CategoryItem) {
    if (selected.has(cat.slug)) {
      onChange(value.filter((v) => v.slug !== cat.slug));
      return;
    }
    if (!isPro) {
      onChange([cat]); // radio: replace whatever was picked
      return;
    }
    if (value.length >= max) return; // Pro cap reached
    onChange([...value, cat]);
  }

  // A dense pill-wrap grid: chips wrap across the row so the ~10 quick-pick
  // categories fit without a tall scroll, while min-h keeps each a comfortable
  // mobile tap target. Categories beyond these are reached via the search box.
  return (
    <div className="flex flex-wrap gap-1.5">
      {visible.map((cat) => {
        const isSel = selected.has(cat.slug);
        const atCap = isPro && !isSel && value.length >= max;
        return (
          <button
            key={cat.slug}
            type="button"
            aria-pressed={isSel}
            disabled={atCap}
            onClick={() => toggle(cat)}
            title={singleLine ? cat.name : undefined}
            className={`${
              isSel
                ? 'inline-flex min-h-[36px] items-center rounded-full border-[1.5px] border-brand bg-brand/10 px-3 py-1 text-[12.5px] font-semibold leading-tight text-brand'
                : 'inline-flex min-h-[36px] items-center rounded-full border border-edge bg-bone-surface px-3 py-1 text-[12.5px] leading-tight text-ink-soft transition-colors hover:border-brand/40 disabled:cursor-not-allowed disabled:opacity-40'
            }${singleLine ? ' max-w-full whitespace-nowrap' : ''}`}
          >
            {singleLine ? <span className="min-w-0 truncate">{cat.name}</span> : cat.name}
          </button>
        );
      })}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => (onMore ? onMore() : setExpanded(true))}
          className="inline-flex min-h-[36px] items-center rounded-full border border-dashed border-brand/50 bg-bone-surface px-3 py-1 text-[12.5px] font-semibold leading-tight text-brand transition-colors hover:border-brand"
        >
          {moreLabel ? moreLabel(hidden) : `+${hidden}`}
        </button>
      )}
    </div>
  );
}
